<template>
	<Layout pageTitle="Retirar dinero" :backlink="`/withdraws`">
		<Loader :show="loading || saving" />
		<div class="main-container container" v-if="!loading && accountDashboard">
			<div
				class="alert alert-info"
				v-if="accountDashboard && accountDashboard.account && accountDashboard.account.bankAccounts.length == 0"
			>
				<div>
					<h5>No tienes una cuenta bancaria configurada</h5>
					<p>Para poder retirar dinero, debes configurar al menos una cuenta bancaria</p>
				</div>
				<div>
					<router-link tag="a" to="/settings/bank-accounts" class="btn">Agregar cuenta bancaria</router-link>
				</div>
			</div>
			<div class="row row-xs mt-4 mb-4 pb-4">
				<div class="col-12 col-md-8 col-xl-6 m-auto">
					<div class="card " v-if="!done">
						<div class="card-body p-3">
							<h5 class="mb-0">¿Cuánto quieres retirar?</h5>

							<div class="input-price mt-5" @click="focus" :class="{ hasError: error }">
								<span class="prefix">{{ accountDashboard.account.currencySymbol }}</span>
								<currency-input
									ref="inputPrice"
									v-model="form.amount"
									:options="{ currency: 'UYU', currencyDisplay: 'hidden' }"
									:style="`width: ${form.amount ? form.amount.toString().length * 26 : 36}px`"
									@input="onInput"
								>
								</currency-input>
							</div>

							<div class="text-center mt-3 text-danger" v-if="error && message">
								{{ message }}
							</div>
							<div class="text-center mt-3" v-else>
								De {{ accountDashboard.account.currencySymbol }}
								{{ accountDashboard.released | toCurrency }}
							</div>

							<FormulateInput
								v-if="showBankSelector"
								class="mt-5"
								v-model="form.bankAccountId"
								type="select"
								label="Cuenta bancaria"
								:options="
									accountDashboard.account.bankAccounts.map((bankAccount) => {
										return {
											value: bankAccount.id,
											label:
												bankAccount.bank.name +
												' - ' +
												bankAccount.type +
												' ' +
												bankAccount.number +
												' - ' +
												bankAccount.name
										}
									})
								"
							/>

							<div
								style="max-width: 300px"
								class="m-auto"
								v-if="accountDashboard.account.applyTransferCommission && selectedBank"
							>
								<table class="table table-sm table-borderless mt-5">
									<tr>
										<td>
											Comisión por transferencia
										</td>
										<td class="text-right">
											{{ accountDashboard.account.currencySymbol }}
											{{ selectedBank.bank.transferCommission | toCurrency }}
										</td>
									</tr>
									<tr>
										<td>
											<strong>Total a recibir:</strong>
										</td>
										<td class="text-right">
											<strong>
												{{ accountDashboard.account.currencySymbol }}
												{{ totalToReceive | toCurrency }}
											</strong>
										</td>
									</tr>
								</table>
							</div>

							<div class="d-flex justify-content-end">
								<div class="btn btn-primary mt-4" @click="save">Continuar</div>
							</div>
						</div>
					</div>

					<div class="card " v-if="done">
						<div class="card-body p-3 text-center">
							<div class="svg-box">
								<svg class="circular green-stroke">
									<circle
										class="path"
										cx="75"
										cy="75"
										r="50"
										fill="none"
										stroke-width="5"
										stroke-miterlimit="10"
									/>
								</svg>
								<svg class="checkmark green-stroke">
									<g transform="matrix(0.79961,8.65821e-32,8.39584e-32,0.79961,-489.57,-205.679)">
										<path
											class="checkmark__check"
											fill="none"
											d="M616.306,283.025L634.087,300.805L673.361,261.53"
										/>
									</g>
								</svg>
							</div>

							<h5 class="mb-0">Listo!</h5>
							<p>
								En menos de 48 horas hábiles, tus {{ accountDashboard.account.currencySymbol }}
								{{ totalToReceive }} serán depositados en la cuenta del {{ selectedBank.bank.name }}
							</p>
							<router-link tag="a" to="/withdraws" class="btn btn-secondary">Volver</router-link>
						</div>
					</div>
				</div>
			</div>
		</div>
	</Layout>
</template>

<script>
import gql from "graphql-tag"
import mixins from "@/mixins/mixins"
import formMixins from "@/mixins/formMixins"
import CurrencyInput from "@/components/CurrencyInput"

export default {
	name: "WithdrawForm",
	components: { CurrencyInput },
	mixins: [mixins, formMixins],
	data() {
		return {
			accountDashboard: null,
			form: {
				amount: 0,
				bankAccountId: null
			},
			showBankSelector: false,
			error: false,
			message: "",
			loading: true,
			saving: false,
			done: false
		}
	},
	computed: {
		selectedBank: function() {
			return this.accountDashboard?.account?.bankAccounts.find(
				(bankAccount) => bankAccount.id === this.form.bankAccountId
			)
		},
		totalToReceive: function() {
			if (this.selectedBank && this.form.amount) {
				let toReceive = this.form.amount
				if (this.accountDashboard.account.applyTransferCommission) {
					toReceive = this.form.amount - this.selectedBank.bank.transferCommission
				}

				return toReceive > 0 ? toReceive : 0
			} else {
				return 0
			}
		}
	},

	async beforeMount() {
		this.loading = true
		this.accountDashboard = await this.fetchData()

		this.loading = false
	},

	methods: {
		onInput(e) {
			this.message = `De ${this.accountDashboard.account.currencySymbol} ${this.accountDashboard.released}`
			this.error = false
			if (e > this.accountDashboard.released) {
				this.error = true
				this.message = `No puedes retirar más dinero de lo que tienes en tu cuenta`
			}

			if (
				this.accountDashboard.account.applyTransferCommission &&
				this.selectedBank &&
				e < this.selectedBank.bank.transferCommission
			) {
				this.error = true
				this.message = `Debes retirar al menos ${this.selectedBank.bank.transferCommission} ${this.accountDashboard.account.currencySymbol}`
			}
			if (e < 0) {
				this.error = true
				this.message = `No puedes retirar una cantidad negativa`
			}
		},
		focus() {
			this.$refs.inputPrice.$el.focus()
		},

		async fetchData() {
			this.loading = true
			return this.$apollo
				.query({
					query: gql`
						query {
							accountDashboard {
								account {
									name

									currencySymbol
									bankAccounts {
										id
										name
										type
										number
										bank {
											id
											name
											transferCommission
										}
									}
									applyTransferCommission
								}
								released
								toRelease
							}
						}
					`,
					fetchPolicy: "no-cache"
				})
				.then(({ data }) => {
					this.loading = false

					if (data.accountDashboard.account.bankAccounts.length === 1) {
						this.form.bankAccountId = data.accountDashboard.account.bankAccounts[0].id
					} else {
						this.showBankSelector = true
					}

					return data.accountDashboard
				})
		},

		async save() {
			if (!this.saving && !this.error && this.form.bankAccountId && this.form.amount > 0) {
				this.saving = true

				let data = {
					amount: this.form.amount,
					bankAccountId: this.form.bankAccountId
				}

				return this.$apollo
					.mutate({
						mutation: gql`
							mutation($data: WithdrawInput!) {
								withdrawCreate(data: $data) {
									id
								}
							}
						`,
						variables: {
							data
						}
					})
					.then(() => {
						this.loading = false
						this.saving = false
						this.done = true
					})
					.catch((error) => {
						this.parseGQLErrors(error)
						this.loading = false
					})
			}

			this.saving = false
		}
	}
}
</script>

<style scoped lang="scss">
.input-price {
	display: flex;
	align-items: center;
	justify-content: center;
	border-bottom: 2px solid #b9b6b6;
	text-align: center;

	.prefix {
		font-size: 1.4rem;
		color: #616161;
		margin-right: 8px;
	}

	& /deep/ input {
		font-size: 3.125em;
		border-bottom: 0;
		height: auto;
		font-weight: 300;
		color: #666;
		min-width: 0.25em;
		line-height: 60px;
		text-align: inherit;
		padding: 0;
		border: none;

		&:focus {
			outline: none;
		}
	}
	&.hasError {
		border-bottom: 2px solid #e53935;
	}
	&:focus {
		border-bottom: 2px solid #00a8ff;
	}
}

.svg-box {
	display: inline-block;
	position: relative;
	width: 150px;
}

.green-stroke {
	stroke: #7cb342;
}

.red-stroke {
	stroke: #ff6245;
}

.yellow-stroke {
	stroke: #ffc107;
}

.circular circle.path {
	stroke-dasharray: 330;
	stroke-dashoffset: 0;
	stroke-linecap: round;
	opacity: 0.4;
	animation: 0.7s draw-circle ease-out;
}

/*------- Checkmark ---------*/
.checkmark {
	stroke-width: 6.25;
	stroke-linecap: round;
	position: absolute;
	top: 56px;
	left: 49px;
	width: 52px;
	height: 40px;
}

.checkmark path {
	animation: 1s draw-check ease-out;
}

@keyframes draw-circle {
	0% {
		stroke-dasharray: 0, 330;
		stroke-dashoffset: 0;
		opacity: 1;
	}

	80% {
		stroke-dasharray: 330, 330;
		stroke-dashoffset: 0;
		opacity: 1;
	}

	100% {
		opacity: 0.4;
	}
}

@keyframes draw-check {
	0% {
		stroke-dasharray: 49, 80;
		stroke-dashoffset: 48;
		opacity: 0;
	}

	50% {
		stroke-dasharray: 49, 80;
		stroke-dashoffset: 48;
		opacity: 1;
	}

	100% {
		stroke-dasharray: 130, 80;
		stroke-dashoffset: 48;
	}
}

/*---------- Cross ----------*/

.cross {
	stroke-width: 6.25;
	stroke-linecap: round;
	position: absolute;
	top: 54px;
	left: 54px;
	width: 40px;
	height: 40px;
}

.cross .first-line {
	animation: 0.7s draw-first-line ease-out;
}

.cross .second-line {
	animation: 0.7s draw-second-line ease-out;
}

@keyframes draw-first-line {
	0% {
		stroke-dasharray: 0, 56;
		stroke-dashoffset: 0;
	}

	50% {
		stroke-dasharray: 0, 56;
		stroke-dashoffset: 0;
	}

	100% {
		stroke-dasharray: 56, 330;
		stroke-dashoffset: 0;
	}
}

@keyframes draw-second-line {
	0% {
		stroke-dasharray: 0, 55;
		stroke-dashoffset: 1;
	}

	50% {
		stroke-dasharray: 0, 55;
		stroke-dashoffset: 1;
	}

	100% {
		stroke-dasharray: 55, 0;
		stroke-dashoffset: 70;
	}
}

.alert-sign {
	stroke-width: 6.25;
	stroke-linecap: round;
	position: absolute;
	top: 40px;
	left: 68px;
	width: 15px;
	height: 70px;
	animation: 0.5s alert-sign-bounce cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.alert-sign .dot {
	stroke: none;
	fill: #ffc107;
}

@keyframes alert-sign-bounce {
	0% {
		transform: scale(0);
		opacity: 0;
	}

	50% {
		transform: scale(0);
		opacity: 1;
	}

	100% {
		transform: scale(1);
	}
}
</style>
